import { Component, Inject, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  PartnersService,
  Partner,
  PartnerConfigJson,
  PartnerInviteEmailConfig,
} from '../../../services/partners/partners.service';
import { RewardsAdminService } from '../../../services/rewards-admin/rewards-admin.service';

@Component({
    selector: 'app-cobranding-form',
    templateUrl: './cobranding-form.component.html',
    styleUrls: ['./cobranding-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CobrandingFormComponent implements OnInit {
  public form = this.formBuilder.group({
    name: ['', Validators.required],
    url: ['', Validators.required],
    tagline: [''],
    primary: ['#FFFFFF', Validators.required],
    secondary: ['#FFFFFF', Validators.required],
    wallitTertiaryColor: ['#3AE3A0', Validators.required],
    wallitGradientSWColor: ['#FF005D', Validators.required],
    wallitGradientNEColor: ['#FF4139', Validators.required],
    signupDisabled: [false],
    inviteEmailDownloadUrl: [''],
    configjson: this.formBuilder.control<any>({}),
  });

  public partner: Partner | null = null;
  public isEdit = false;
  public invalidField = '';
  public rewardsInBands: Array<{ title: string; reward: string }> = [];
  private rewards: any[] = [];
  private lastBrand = '';

  public logoPreview = '';
  public lightLogoPreview = '';
  public horizontalLogoPreview = '';
  public horizontalLightLogoPreview = '';
  public inviteQrPreview = '';
  public savedInviteDownloadUrl = '';

  private logoFile: File | null = null;
  private lightLogoFile: File | null = null;
  private horizontalLogoFile: File | null = null;
  private horizontalLightLogoFile: File | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private partnersService: PartnersService,
    private rewardsAdminService: RewardsAdminService,
    private snackBar: MatSnackBar,
    @Optional() private dialogRef?: MatDialogRef<CobrandingFormComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: { partner?: Partner },
  ) {}

  ngOnInit(): void {
    if (this.dialogData?.partner) {
      this.partner = this.dialogData.partner;
      this.isEdit = true;
      const inviteEmailConfig = this.getInviteEmailConfig(this.partner.configjson);
      this.logoPreview = this.partner.logoUrl || '';
      this.lightLogoPreview = this.partner.lightLogoUrl || '';
      this.horizontalLogoPreview = this.partner.horizontalLogoUrl || '';
      this.horizontalLightLogoPreview = this.partner.horizontalLogoOnWhite || '';
      this.inviteQrPreview = inviteEmailConfig.app?.qrCodeUrl || '';
      this.savedInviteDownloadUrl = inviteEmailConfig.app?.downloadUrl || '';
      this.form.patchValue({
        name: this.partner.name,
        url: this.getUrlPrefix(this.partner.redirectUrl),
        tagline: this.partner.tagline,
        primary: this.partner.ourlyPrimaryColor,
        secondary: this.partner.ourlySecondaryColor,
        wallitTertiaryColor: this.partner.wallitTertiaryColor,
        wallitGradientSWColor: this.partner.wallitGradientSWColor,
        wallitGradientNEColor: this.partner.wallitGradientNEColor,
        signupDisabled: this.partner.signupDisabled,
        configjson: this.partner.configjson || {},
        inviteEmailDownloadUrl: inviteEmailConfig.app?.downloadUrl || '',
      });
    }

    this.form.get('configjson')?.valueChanges.subscribe(() => this.buildRewardsInBands());
    this.form.get('url')?.valueChanges.subscribe((value) => this.loadActiveRewards(value || ''));
    const initialUrl = this.form.get('url')?.value || '';
    if (initialUrl) {
      this.loadActiveRewards(initialUrl);
    }
  }

  getUrlPrefix(redirectUrl?: string): string {
    if (!redirectUrl) {
      return '';
    }
    const parts = redirectUrl.split('.');
    if (parts.length <= 2) {
      return parts[0];
    }
    return parts.slice(0, -2).join('.');
  }

  private async loadActiveRewards(brand: string): Promise<void> {
    const trimmed = brand.trim();
    if (!trimmed || trimmed === this.lastBrand) {
      return;
    }
    this.lastBrand = trimmed;
    try {
      const rewards = await this.rewardsAdminService.getActiveRewards(trimmed);
      this.rewards = rewards || [];
      this.buildRewardsInBands();
    } catch (error) {
      console.error('Error loading active rewards', error);
    }
  }

  changeColor(event: any): void {
    let value = event.target.value.replace(/[^0-9a-fA-F#]+/g, '');
    if (value.length > 7) {
      value = value.substring(0, 7);
    }
    event.target.value = value;
  }

  onFileChange(event: any, type: 'logo' | 'light' | 'horizontal' | 'horizontalLight'): void {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.includes('image')) {
      this.invalidField = 'Invalid file type selected. Must be image.';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const result = e.target?.result || '';
      if (type === 'logo') {
        this.logoPreview = result;
      } else if (type === 'light') {
        this.lightLogoPreview = result;
      } else if (type === 'horizontal') {
        this.horizontalLogoPreview = result;

      } else {
        this.horizontalLightLogoPreview = result;
      }
    };
    reader.readAsDataURL(file);
    if (type === 'logo') {
      this.logoFile = file;
    } else if (type === 'light') {
      this.lightLogoFile = file;
    } else if (type === 'horizontal') {
      this.horizontalLogoFile = file;

    } else {
      this.horizontalLightLogoFile = file;
    }
  }

  buildRewardsInBands(): void {
    const rawValue = this.form.get('configjson')?.value;
    const configJson = this.parseJsonValue(rawValue);
    if (!configJson || !configJson.bands) {
      this.rewardsInBands = [];
      return;
    }
    const rewards = this.rewards || [];
    const bands = configJson.bands as any[];
    const mapped: Array<{ title: string; reward: string }> = [];
    bands.forEach((band) => {
      if (band.type === 'cj-carousel' && band.contentType === 'merchant') {
        const filteredRewards = rewards.filter((reward: any) =>
          reward.bandCategories?.find((category: string) =>
            !band.categories || band.categories.find((bandCategory: string) => category === bandCategory),
          ),
        );
        if (filteredRewards.length > 0) {
          mapped.push({
            title: band.title,
            reward: filteredRewards.map((reward: any) => reward['advertiser-name']).join(', '),
          });
        }
      }
    });
    this.rewardsInBands = mapped;
  }

  private parseJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return value ? JSON.parse(value) : {};
      } catch {
        return value;
      }
    }
    return value ?? {};
  }

  async save(): Promise<void> {
    this.invalidField = '';
    if (this.form.invalid) {
      this.invalidField = 'Fill all required fields above';
      return;
    }
    const downloadUrl = `${this.form.get('inviteEmailDownloadUrl')?.value || ''}`.trim();
    if (downloadUrl) {
      try {
        const branchUrl = new URL(downloadUrl);
        if (branchUrl.protocol !== 'https:' || branchUrl.username || branchUrl.password) throw new Error();
      } catch {
        this.invalidField = 'Enter a valid HTTPS Branch link for this brand.';
        return;
      }
    }
    const url = this.form.get('url')?.value;
    const redirectUrl = url ? `${url}.wallit.app` : '';
    const payloadConfigjson = this.buildPartnerConfigjson();
    const payload: any = {
      ...(this.partner || {}),
      name: this.form.get('name')?.value,
      redirectUrl,
      domain: redirectUrl,
      shortName: redirectUrl,
      tagline: this.form.get('tagline')?.value,
      ourlyPrimaryColor: this.form.get('primary')?.value,
      ourlySecondaryColor: this.form.get('secondary')?.value,
      wallitTertiaryColor: this.form.get('wallitTertiaryColor')?.value,
      wallitGradientSWColor: this.form.get('wallitGradientSWColor')?.value,
      wallitGradientNEColor: this.form.get('wallitGradientNEColor')?.value,
      signupDisabled: this.form.get('signupDisabled')?.value,
      configjson: payloadConfigjson,
    };

    try {
      if (this.isEdit && this.partner?.partnerId) {
        if (this.logoFile) {
          payload.logoUrl = '';
        }
        if (this.lightLogoFile) {
          payload.lightLogoUrl = '';
        }
        if (this.horizontalLogoFile) {
          payload.horizontalLogoUrl = '';
        }
        if (this.horizontalLightLogoFile) {
          payload.horizontalLogoOnWhite = '';
        }
        await this.partnersService.updatePartner(payload);
        await this.uploadAssets(this.partner.partnerId);
        this.snackBar.open('Co-branding updated', 'Close', { duration: 2000 });
        this.dialogRef?.close(true);
      } else {
        if (!this.logoFile) {
          this.invalidField = 'Logo on light is required.';
          return;
        }
        const created = await this.partnersService.createPartner(payload);
        await this.uploadAssets(created.partnerId || payload.partnerId || '');
        this.snackBar.open('Co-branding created', 'Close', { duration: 2000 });
        this.dialogRef?.close(true);
      }
    } catch (error) {
      console.error('Error saving co-branding', error);
      this.snackBar.open('Error saving co-branding', 'Close', { duration: 2000 });
    }
  }

  private async uploadAssets(partnerId: string): Promise<void> {
    const uploads: Array<Promise<void>> = [];
    if (this.logoFile) {
      uploads.push(this.partnersService.changePartnerLogo(partnerId, this.logoFile as any, 'avatar'));
    }
    if (this.lightLogoFile) {
      uploads.push(this.partnersService.changePartnerLogo(partnerId, this.lightLogoFile as any, 'lightlogo'));
    }
    if (this.horizontalLogoFile) {
      uploads.push(this.partnersService.changePartnerLogo(partnerId, this.horizontalLogoFile as any, 'horizontallogo'));
    }
    if (this.horizontalLightLogoFile) {
      uploads.push(this.partnersService.changePartnerLogo(partnerId, this.horizontalLightLogoFile as any, 'horizontallightlogo'));
    }
    await Promise.all(uploads);
  }

  private getInviteEmailConfig(configjson: any): PartnerInviteEmailConfig {
    const parsedConfig = this.parseJsonValue(configjson) as PartnerConfigJson;
    return {
      mode: parsedConfig?.inviteEmail?.mode === 'app' ? 'app' : 'web',
      app: {
        ...(parsedConfig?.inviteEmail?.app?.downloadUrl ? {downloadUrl: `${parsedConfig.inviteEmail.app.downloadUrl}`.trim()} : {}),
        ...(parsedConfig?.inviteEmail?.app?.qrCodeUrl ? {qrCodeUrl: `${parsedConfig.inviteEmail.app.qrCodeUrl}`.trim()} : {}),
      },
    };
  }

  private buildPartnerConfigjson(): PartnerConfigJson {
    const configjson = this.parseJsonValue(this.form.get('configjson')?.value) as PartnerConfigJson;
    const downloadUrl = `${this.form.get('inviteEmailDownloadUrl')?.value || ''}`.trim();
    const qrCodeUrl = `${this.getInviteEmailConfig(configjson).app?.qrCodeUrl || this.inviteQrPreview || ''}`.trim();

    return {
      ...(configjson || {}),
      inviteEmail: {
        mode: downloadUrl ? 'app' : this.getInviteEmailConfig(configjson).mode,
        app: {
          ...(downloadUrl ? {downloadUrl} : {}),
          ...(qrCodeUrl ? {qrCodeUrl} : {}),
        },
      },
    };
  }

  cancel(): void {
    this.dialogRef?.close(false);
  }
}
